/**
 * The equipment dialog's brew-day reminders: jobs only the kit knows about,
 * like swapping the condensate bucket on a steam condenser or putting the
 * immersion chiller in to sanitise, on the same clock as the hop additions. A
 * reminder repeats every so many minutes, or comes once at a time left in the
 * boil.
 *
 * ## Why the minutes text is not held here
 *
 * The minutes box has the same problem the dialog's numeric fields do: a box
 * whose `value` is formatted from the parsed number cannot pass through `3` on
 * the way to `30`, or be emptied to be retyped. So the typed text is the state.
 * But the dialog's `validate()` has to read that text on Save, so the text
 * lives in the dialog, keyed by reminder id, and arrives here as
 * `minutesTexts`. A row nobody has typed into has no entry and shows its saved
 * figure. This component stays presentational: it owns no state at all.
 */

import type { BrewReminder } from '@beerwright/commands';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { newId } from '@/lib/ids';

/** The shortest interval a reminder may repeat at, matching `brewReminderSchema`. */
export const REMINDER_MIN_MINUTES = 5;
/** The fewest minutes left a once reminder may come at, matching `brewReminderSchema`. */
export const REMINDER_MIN_MINUTES_LEFT = 1;
/** The longest either may be, matching `brewReminderSchema`. */
export const REMINDER_MAX_MINUTES = 240;
/** How many reminders a profile may carry, matching `BREW_REMINDERS_FIELD`. */
export const MAX_BREW_REMINDERS = 8;

/** The fewest minutes this reminder's kind allows. */
export function reminderMinMinutes(reminder: BrewReminder): number {
  return 'everyMinutes' in reminder ? REMINDER_MIN_MINUTES : REMINDER_MIN_MINUTES_LEFT;
}

/**
 * Typed minutes as a whole number in the reminder's range, or `null` when the
 * text is anything else. Both the row's keystroke handler and the dialog's
 * `validate()` read the text through this, so they cannot disagree.
 */
export function parseReminderMinutes(text: string, reminder: BrewReminder): number | null {
  const trimmed = text.trim();
  if (!/^\d{1,4}$/.test(trimmed)) return null;
  const minutes = Number(trimmed);
  return minutes >= reminderMinMinutes(reminder) && minutes <= REMINDER_MAX_MINUTES
    ? minutes
    : null;
}

/** The reminder's saved minutes, whichever kind it is. */
function savedMinutes(reminder: BrewReminder): number {
  return 'everyMinutes' in reminder ? reminder.everyMinutes : reminder.atMinutesLeft;
}

/** The reminder with its minutes replaced, keeping its kind. */
function withMinutes(reminder: BrewReminder, minutes: number): BrewReminder {
  return 'everyMinutes' in reminder
    ? { ...reminder, everyMinutes: minutes }
    : { ...reminder, atMinutesLeft: minutes };
}

/**
 * The reminder switched to the other kind, carrying its minutes across. A
 * figure the new kind refuses (2 minutes, switched to repeating) stays in the
 * draft for Save to complain about, as a mistyped one would.
 */
function switchKind(reminder: BrewReminder, repeats: boolean): BrewReminder {
  const { id, label, window } = reminder;
  const minutes = savedMinutes(reminder);
  return repeats
    ? { id, label, window, everyMinutes: minutes }
    : { id, label, window, atMinutesLeft: minutes };
}

/** What a row's minutes box shows: the typed text if any, else the saved figure. */
export function reminderMinutesText(
  reminder: BrewReminder,
  minutesTexts: Readonly<Record<string, string>>,
): string {
  return minutesTexts[reminder.id] ?? String(savedMinutes(reminder));
}

export function BrewRemindersField({
  reminders,
  minutesTexts,
  problems,
  onChange,
  onMinutesTextChange,
  disabled = false,
}: {
  readonly reminders: readonly BrewReminder[];
  /** Typed minutes per reminder id; see the module comment. */
  readonly minutesTexts: Readonly<Record<string, string>>;
  /** The dialog's problems. Only those keyed `brewReminders.<index>` matter here. */
  readonly problems: readonly { readonly key: string }[];
  readonly onChange: (next: readonly BrewReminder[]) => void;
  readonly onMinutesTextChange: (id: string, text: string) => void;
  readonly disabled?: boolean;
}) {
  const replace = (index: number, reminder: BrewReminder): void =>
    onChange(reminders.map((candidate, i) => (i === index ? reminder : candidate)));

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        Brew-day reminders
      </legend>
      <p className="text-xs text-muted-foreground/80">
        We'll remind you through the boil, on the same clock as your hop additions. Handy for jobs
        specific to your setup, like swapping the condensate bucket on a steam condenser every 30
        minutes, or putting the immersion chiller in at 15 minutes left.
      </p>

      {reminders.length === 0 ? (
        <p className="text-sm text-muted-foreground">No reminders yet.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {reminders.map((reminder, index) => {
            const text = reminderMinutesText(reminder, minutesTexts);
            // A row's problems are keyed by position, not by box, so each box
            // is marked only when the row is flagged and it is the one at fault.
            const flagged = problems.some((problem) => problem.key === `brewReminders.${index}`);
            const repeats = 'everyMinutes' in reminder;
            const labelId = `eq-br-${reminder.id}-label`;
            const whenId = `eq-br-${reminder.id}-when`;
            const minutesId = `eq-br-${reminder.id}-minutes`;
            return (
              // Wraps on a phone: the label takes the first line, the timing
              // and the remove button the second.
              <li key={reminder.id} className="flex flex-wrap items-end gap-2">
                <div className="flex min-w-48 flex-1 flex-col gap-1.5">
                  <Label htmlFor={labelId} className="text-xs">
                    Reminder
                  </Label>
                  <Input
                    id={labelId}
                    type="text"
                    maxLength={60}
                    disabled={disabled}
                    aria-invalid={(flagged && reminder.label.trim() === '') || undefined}
                    className="h-8 text-sm"
                    value={reminder.label}
                    onChange={(event) => replace(index, { ...reminder, label: event.target.value })}
                  />
                </div>
                <div className="flex w-28 shrink-0 flex-col gap-1.5">
                  <Label htmlFor={whenId} className="text-xs">
                    When
                  </Label>
                  <Select
                    value={repeats ? 'every' : 'once'}
                    disabled={disabled}
                    onValueChange={(value) => {
                      if ((value === 'every') !== repeats) {
                        replace(index, switchKind(reminder, value === 'every'));
                      }
                    }}
                  >
                    <SelectTrigger id={whenId} size="sm" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="every">Every</SelectItem>
                      <SelectItem value="once">Once at</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex w-20 shrink-0 flex-col gap-1.5">
                  <Label htmlFor={minutesId} className="text-xs">
                    {repeats ? 'Minutes' : 'Min left'}
                  </Label>
                  <Input
                    id={minutesId}
                    type="text"
                    inputMode="numeric"
                    disabled={disabled}
                    aria-invalid={
                      (flagged && parseReminderMinutes(text, reminder) === null) || undefined
                    }
                    className="h-8 font-mono text-sm tabular-nums"
                    value={text}
                    onChange={(event) => {
                      const typed = event.target.value;
                      onMinutesTextChange(reminder.id, typed);
                      // Only a usable figure reaches the draft; anything else
                      // leaves the last good one there until Save complains.
                      const minutes = parseReminderMinutes(typed, reminder);
                      if (minutes !== null && minutes !== savedMinutes(reminder)) {
                        replace(index, withMinutes(reminder, minutes));
                      }
                    }}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0 text-muted-foreground"
                  disabled={disabled}
                  aria-label={
                    reminder.label.trim() === ''
                      ? 'Remove reminder'
                      : `Remove reminder "${reminder.label.trim()}"`
                  }
                  onClick={() => onChange(reminders.filter((_, i) => i !== index))}
                >
                  <Trash2Icon />
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit"
        disabled={disabled || reminders.length >= MAX_BREW_REMINDERS}
        onClick={() =>
          onChange([...reminders, { id: newId('br'), label: '', everyMinutes: 30, window: 'boil' }])
        }
      >
        <PlusIcon />
        Add reminder
      </Button>
    </fieldset>
  );
}
